import { useState } from "react";
import { Check, ChevronsUpDown, Loader2, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/query-client";
import { useToast } from "@/hooks/use-toast";

interface Carrier {
  id: string;
  name: string;
}

interface CarrierComboboxProps {
  value?: string | null;
  onChange: (carrierId: string | null) => void;
  placeholder?: string;
  'data-testid'?: string;
}

export function CarrierCombobox({ value, onChange, placeholder = "Select carrier...", 'data-testid': dataTestId }: CarrierComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [created, setCreated] = useState<Carrier | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data } = useQuery<{ carriers: Carrier[]; total: number }>({
    queryKey: ["/api/carriers", { limit: 1000 }],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/carriers?limit=1000");
      return res.json();
    },
  });

  const carriers = data?.carriers ?? [];

  const createMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await apiRequest("POST", "/api/carriers", { name });
      return res.json() as Promise<Carrier>;
    },
    onSuccess: async (carrier) => {
      // Kept here so the carrier shows as selected even when the refreshed list does not have it yet.
      setCreated(carrier);
      await queryClient.refetchQueries({ queryKey: ["/api/carriers", { limit: 1000 }] }).catch(() => {});
      onChange(carrier.id);
      setOpen(false);
      setSearch("");
    },
    onError: (error: unknown) => {
      toast({
        title: "Failed to create carrier",
        description: error instanceof Error ? error.message : "An unexpected error occurred.",
        variant: "destructive",
      });
    },
  });

  const selected = carriers.find((c) => c.id === value) ?? (created && created.id === value ? created : undefined);
  const filtered = carriers.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );
  const canCreate = search.trim().length > 0 && !filtered.some(
    (c) => c.name.toLowerCase() === search.trim().toLowerCase()
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid={dataTestId}
        >
          <span className={cn(!selected && "text-muted-foreground")}>
            {selected ? selected.name : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search carriers..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {canCreate ? (
                <button
                  className="flex items-center gap-2 px-2 py-1.5 text-sm w-full hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={createMutation.isPending}
                  onClick={() => createMutation.mutate(search.trim())}
                >
                  {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  Add &quot;{search.trim()}&quot;
                </button>
              ) : (
                "No carriers found."
              )}
            </CommandEmpty>
            <CommandGroup>
              {selected && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => { onChange(null); setOpen(false); setSearch(""); }}
                >
                  <Check className="mr-2 h-4 w-4 opacity-0" />
                  <span className="text-muted-foreground">Clear selection</span>
                </CommandItem>
              )}
              {filtered.map((carrier) => (
                <CommandItem
                  key={carrier.id}
                  value={carrier.name}
                  onSelect={() => {
                    onChange(carrier.id);
                    setOpen(false);
                    setSearch("");
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", carrier.id === value ? "opacity-100" : "opacity-0")} />
                  {carrier.name}
                </CommandItem>
              ))}
              {canCreate && filtered.length > 0 && (
                <CommandItem
                  value={`__create__${search}`}
                  disabled={createMutation.isPending}
                  onSelect={() => { if (!createMutation.isPending) createMutation.mutate(search.trim()); }}
                >
                  {createMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                  Add &quot;{search.trim()}&quot;
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
